import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  Select,
  MenuItem,
  Grid,
  Button,
  CircularProgress,
  Alert
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import { format } from 'date-fns';
import { apiService } from '../../services/api';

const SupplyOrderReport = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [supplyData, setSupplyData] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [deletionDate, setDeletionDate] = useState(null);

  const fetchSupplyData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiService.getSupplyOrderReport({
        month: selectedMonth,
        year: selectedYear
      });
      
      setSupplyData(response.data);
      setDeletionDate(response.deletion_date);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching supply data:', err);
      setError('Failed to load supply data: ' + err.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSupplyData();
  }, [selectedMonth, selectedYear]);

  const handleDownloadReport = async () => {
    try {
      setDownloading(true);
      await apiService.downloadSupplyOrderReport(selectedMonth, selectedYear);
      setDownloading(false);
    } catch (err) {
      console.error('Error downloading report:', err);
      setError('Failed to download report: ' + err.message);
      setDownloading(false);
    }
  };

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  const years = Array.from({ length: 3 }, (_, i) => {
    const year = new Date().getFullYear() - 1 + i;
    return { value: year, label: year.toString() };
  });

  return (
    <Box>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">Supply Order Report</Typography>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
              >
                {months.map((month) => (
                  <MenuItem key={month.value} value={month.value}>
                    {month.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 100 }}>
              <Select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
              >
                {years.map((year) => (
                  <MenuItem key={year.value} value={year.value}>
                    {year.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              startIcon={<RefreshIcon />}
              onClick={fetchSupplyData}
              disabled={loading}
            >
              Refresh
            </Button>

            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadReport}
              disabled={downloading || !supplyData}
            >
              {downloading ? 'Downloading...' : 'Download Report'}
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {deletionDate && (
          <Alert severity="info" sx={{ mb: 3 }}>
            This report will be automatically deleted on {format(new Date(deletionDate), 'MMMM d, yyyy')}.
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : supplyData ? (
          <Grid container spacing={3}>
            {/* Branch-specific tables */}
            {supplyData.branches.map((branch) => (
              <Grid item xs={12} key={branch.name}>
                <Typography variant="subtitle1" gutterBottom>
                  {branch.name}
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Item</TableCell>
                        <TableCell align="right">Quantity Needed</TableCell>
                        <TableCell>Notes</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {branch.items.map((item) => (
                        <TableRow key={item.name}>
                          <TableCell>{item.name}</TableCell>
                          <TableCell align="right">{item.quantity}</TableCell>
                          <TableCell>{item.notes}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            ))}

            {/* Total quantities table */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>
                Total Quantities (All Branches)
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Item</TableCell>
                      <TableCell align="right">Total Quantity</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {supplyData.totals.map((item) => (
                      <TableRow key={item.name}>
                        <TableCell>{item.name}</TableCell>
                        <TableCell align="right">{item.quantity}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        ) : (
          <Alert severity="info">
            No supply data available for the selected month
          </Alert>
        )}
      </Paper>
    </Box>
  );
};

export default SupplyOrderReport; 